import React, { useState } from "react";
import { DiscountsQuery } from "src/api/Discount";
import ClassModel from "src/models/ClassModel";
import MembershipOfferingModel, {
  isMembershipOffering,
} from "src/models/MembershipOfferingModel";
import OfferingModel from "src/models/OfferingModel";
import { useApi } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import formatMoney from "src/utils/formatMoney";
import { modals, useModal } from "src/utils/modals";
import { AmountType, DiscountType } from "types/code-generator";
import { BorderButton } from "../buttons/BorderButton";
import InputCheckbox from "../inputs/InputCheckbox";
import Modal from "./Modal";

interface Props {
  offering: ClassModel | OfferingModel | MembershipOfferingModel;
  onCancel?: () => void;
  onSubmit: (discounts) => void;
}

const AddDiscountModal = modals.create((props: Props) => {
  const modal = useModal();
  const store = useStore();
  const [enabledDiscounts, setEnabledDiscounts] = useState(
    !isMembershipOffering(props.offering) ? props.offering.discounts : []
  );

  const discountsQuery = useApi(DiscountsQuery, {
    variables: {
      organizationId: store.organization.id,
      departmentId: store.organization.hasDepartments
        ? props.offering.department?.id
        : store.organization.defaultDepartment.id,
    },
  });

  const discounts = discountsQuery.discounts || [];

  function onSubmit() {
    props.onSubmit(enabledDiscounts);
    modal.hide();
  }

  function onCancel() {
    if (props.onCancel) {
      props.onCancel();
    }

    modal.hide();
  }

  return (
    <Modal
      visible={modal.visible}
      afterClose={modal.remove}
      title="Add Discount"
      handleCancel={onCancel}
      footer={
        <>
          <BorderButton type="submit" onClick={onSubmit}>
            Apply
          </BorderButton>
          <BorderButton onClick={onCancel}>Cancel</BorderButton>
        </>
      }
    >
      {discounts.map((d) => {
        const numbers = `${d.min}–${d.max}`;
        const types =
          d.discountType === DiscountType.MultiStudent
            ? "participants"
            : "offerings";
        const amountTypes =
          d.amountType === AmountType.Dollar
            ? `$${formatMoney(d.amount)}`
            : `${(d.amount * 100).toFixed(2)}%`;

        return (
          <React.Fragment key={d.id}>
            <div className="px-4">
              <InputCheckbox
                onChange={(checked) => {
                  if (checked) {
                    setEnabledDiscounts([...enabledDiscounts, d]);
                  } else {
                    setEnabledDiscounts(
                      enabledDiscounts.filter((cd) => cd.id !== d.id)
                    );
                  }
                }}
                checked={Boolean(enabledDiscounts.find((cd) => cd.id === d.id))}
                className="items-center"
              >
                {numbers} {types}, {amountTypes} off
              </InputCheckbox>
            </div>
            <hr />
          </React.Fragment>
        );
      })}
    </Modal>
  );
});

export default AddDiscountModal;
